import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from "../context/AuthContext";
import { toast } from "react-toastify";
import { diamond } from "../assets";
import { useRates } from '../code/Rates';

const Levels = () => {
  const { selectedCurrency, convertBalance } = useRates();
  const [levels, setLevels] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { currentUser } = useContext(AuthContext);
  const [activatingLevel, setActivatingLevel] = useState(null);
  const [expandedLevel, setExpandedLevel] = useState(null);

  const toggleExpand = (levelId) => {
    setExpandedLevel(expandedLevel === levelId ? null : levelId);
  };

  useEffect(() => {
    const fetchLevels = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/levels-in-group`);
        const data = await response.json();
        if (Array.isArray(data)) {
          setLevels(data);
        }
      } catch (error) {
        console.error("Error fetching levels:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchLevels();
  }, []);

  const handleActivate = async (levelId) => {
    setActivatingLevel(levelId);

    const authToken = localStorage.getItem("accessToken");

    if (!currentUser || !authToken) {
      toast.error("User not authenticated! You have to be logged in.");
      setActivatingLevel(null);
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/upgrade-level`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify({ levelId, userId: currentUser.id }),
        }
      );

      const result = await response.json();

      if (response.ok) {
        toast.success(result.message || "Level upgrade activation successfully!");
      } else {
        throw new Error(result.message || "Activation failed.");
      }
    } catch (error) {
      console.error("Activation error:", error);
      toast.error(error.message);
    } finally {
      setActivatingLevel(null);
    }
  };

  const skeletonLoader = (
    <div className="flex flex-col gap-[2px] md:hidden">
      {[...Array(3)].map((_, index) => (
        <div key={index} className="p-2 bg-gray-200 rounded-md animate-pulse">
          <div className="w-1/2 h-4 bg-gray-300 mb-2"></div>
          {[...Array(3)].map((_, levelIndex) => (
            <div key={levelIndex} className="flex w-full p-2 mb-2 bg-gray-300 rounded-md animate-pulse">
              <div className="w-[33%] h-4 bg-gray-400 rounded-md"></div>
              <div className="w-[33%] h-4 bg-gray-400 rounded-md mx-2"></div>
              <div className="w-[33%] h-4 bg-gray-400 rounded-md"></div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );

  return (
    <div className='flex w-full flex-col gap-2 p-2 h-full'>
      <div className='font-bold my-1 text-[18px] text-center'>Levels</div>
      <div className='flex flex-col w-full'>
        {isLoading ? (
          skeletonLoader
        ) : (
          <div className="flex flex-col gap-[2px] md:hidden h-full overflow-y-auto mb-40">
          {levels.map((item) => (
            <div key={item.id}>
              <div className="font-bold text-[18px] my-1 text-rose-500">{item.title}</div>
              {item.levels.map((level) => (
                <div key={level.id} className="flex flex-col">
                  {/* Clickable Level Item */}
                  <div
                    onClick={() => toggleExpand(level.id)}
                    className="flex w-full p-2 rounded-md text-black bg-slate-50 hover:bg-slate-100 cursor-pointer transition-all duration-300"
                  >
                    <div className="flex w-[33%] items-center justify-start">{level.name} {convertBalance(level.amount)}</div>
                    <div className="flex w-[33%] items-center justify-center">$</div>
                    <div className="flex w-[33%] items-center justify-end">{convertBalance(level.earnings)} per day</div>
                  </div>

                  {/* Expandable Section */}
                  <div
                    className={`flex justify-center overflow-hidden transition-all duration-300 ${
                      expandedLevel === level.id ? "h-auto py-4" : "h-0"
                    }`}
                  >
                    {expandedLevel === level.id && (
                      <button
                        onClick={() => handleActivate(level.id)}
                        disabled={activatingLevel === level.id}
                        className={`text-center text-cyan-800 hover:text-white bg-cyan-200 hover:bg-cyan-500 rounded-[12px] p-4 max-md:w-[100px] md:w-[120px] lg:w-[150px] max-xl:w-[150px] 2xl:w-[200px] ${
                          activatingLevel === level.id ? "opacity-50 cursor-not-allowed" : ""
                        }`}
                      >
                        {activatingLevel === level.id ? "Activating..." : "Activate"}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
        )}

        {isLoading ? (
          <div className="flex flex-wrap w-full justify-evenly gap-[2px] max-md:hidden">
            {[...Array(3)].map((_, index) => (
              <div key={index} className="flex flex-col gap-2 bg-gray-200 p-2 rounded-md animate-pulse">
                <div className="w-1/2 h-4 bg-gray-300 mb-4"></div>
                <div className="flex flex-col items-center">
                  <div className="w-[120px] h-[120px] bg-gray-400 rounded-lg mb-4"></div>
                  <div className="w-[120px] h-4 bg-gray-300 mb-4"></div>
                  <div className="w-[120px] h-4 bg-gray-300 mb-4"></div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-wrap w-full justify-evenly mb-20 gap-[2px] max-md:hidden">
            {levels.map((item) => (
              <div key={item.id}>
                <div className="font-bold text-[18px] my-1 text-rose-500">{item.title}</div>
                {item.levels.map((level) => (
                  <div key={level.id} className='flex flex-col gap-2'>
                    <div className="flex flex-col flex-shrink-0 bg-white hover:bg-slate-50 shadow-lg max-md:h-[100px] max-md:w-[100px] md:h-[120px] md:w-[120px] lg:h-[150px] lg:w-[150px] max-xl:h-[150px] max-xl:w-[150px] 2xl:h-[200px] 2xl:w-[200px] p-2 rounded-[12px] cursor-pointer">
                      <div className="flex w-full justify-between">
                        <div className="flex w-fit items-center justify-start font-bold">{level.name}</div>
                        <div className="flex font-black w-[30px] h-[30px] bg-white border-2 border-slate-300 rounded-full items-center justify-center">$</div>
                      </div>
                      <div className="text-[14px] xl:text-[16px]"><span>{selectedCurrency}</span> {convertBalance(level.amount)}</div>
                      <img src={diamond} alt="Level icon" className="flex object-contain h-[40%] w-full shrink-0" />
                      <div className="flex xl:flex-col w-full gap-2 leading-1 items-center">
                        <div className="flex w-fit items-center justify-start font-bold">Earns</div>
                        <div className="text-[12px] xl:text-[16px]"><span>{convertBalance(level.earnings)}</span> per day</div>
                      </div>
                    </div>
                    <button 
                      onClick={() => handleActivate(level.id)}
                      disabled={activatingLevel === level.id}
                      className={`text-center text-cyan-800 hover:text-white bg-cyan-200 hover:bg-cyan-500 rounded-[12px] p-4 max-md:w-[100px] md:w-[120px] lg:w-[150px] max-xl:w-[150px] 2xl:w-[200px] ${activatingLevel === level.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {activatingLevel === level.id ? "Activating..." : "Activate"}
                    </button>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Levels;
